import type { StatId } from '../domain/stats.ts';
import type { RollMode } from './rollModes.ts';

/**
 * RULE DCC-HIST-001 - application behaviour, not a game rule.
 * The ten most recent DICE ROLLS are retained, newest first, across every roll
 * type: Skill Checks, Stat Checks, Evade Checks, Attack Checks, damage rolls
 * and Skill Advancement Checks. The cap is ten TOTAL, not ten of each kind.
 *
 * Entries store the modifiers actually used at the time. Nothing here ever
 * references the character, so later Stat or Rank changes cannot rewrite an
 * old roll.
 */
export const MAX_ROLL_HISTORY = 10;
export const COMPACT_ROLL_HISTORY = 2;

export type RollHistoryKind = 'skill' | 'stat' | 'evade' | 'attack' | 'damage' | 'advancement';

/**
 * Why a roll came out the way it did, frozen at the moment it was made.
 *
 * A Buff that has since expired must still explain a roll it changed, so every
 * source is copied here as plain text and numbers. Nothing in a history entry
 * consults the currently active Effects.
 *
 * The three kinds are kept apart deliberately:
 *  - 'modifier'      changed a number, and `target` says which;
 *  - 'advantage' /
 *    'disadvantage'  pushed the roll mode, and changed no number.
 */
export interface RollEffectSource {
  kind: 'modifier' | 'advantage' | 'disadvantage';
  /** The Effect's display label, e.g. "Oakhide". */
  label: string;
  /** The Effect's stable source ID. */
  sourceId: string;
  /** What the modifier changed, e.g. "Dex Mod". Null for mode sources. */
  target: string | null;
  /** The amount contributed. Null for mode sources, which change no number. */
  amount: number | null;
}

interface RollHistoryBase {
  id: string;
  timestamp: string;
  /** Label shown in the compact list, e.g. "INT + Negotiation". */
  label: string;
  total: number;
  /**
   * Effects that contributed to this roll. Empty when none did.
   * Always present, so a reader never has to guess whether it was recorded.
   */
  effectSources: RollEffectSource[];
}

/** Any roll resolved on a d20: Skill, Stat, Evade, Attack, Advancement. */
export interface CheckHistoryEntry extends RollHistoryBase {
  rollType: 'skill' | 'stat' | 'evade' | 'attack' | 'advancement';
  statId: StatId | null;
  statAbbr: string | null;
  /** Stat Mod at the time of the roll. Never recalculated. */
  statMod: number | null;
  skillId: string | null;
  skillName: string | null;
  /** Skill Rank at the time of the roll. Never recalculated. */
  skillRank: number | null;
  /** The mode actually used after Effect sources and the player's choice. */
  mode: RollMode;
  /**
   * What the player asked for, before Effect sources were folded in. Kept
   * separate so a later reader can tell a Buff's doing from the player's.
   */
  manualMode: RollMode;
  /** Every d20 face rolled, in order. */
  dice: number[];
  keptDie: number;
  extraComponents: { label: string; value: number }[];
  untrained: boolean;
  /** Difficulty, or the target's Evade for an Attack Check. Null when unknown. */
  difficulty: number | null;
  degree: string | null;
}

/** A damage roll: several dice of one size plus flat modifiers. */
export interface DamageHistoryEntry extends RollHistoryBase {
  rollType: 'damage';
  /** The attack this damage belongs to. */
  skillId: string | null;
  skillName: string | null;
  damageType: string;
  /** Each line of the damage breakdown, with die faces where dice were rolled. */
  components: { label: string; value: number; faces?: number[] }[];
  criticalHit: boolean;
}

export type RollHistoryEntry = CheckHistoryEntry | DamageHistoryEntry;

export function isDamageEntry(entry: RollHistoryEntry): entry is DamageHistoryEntry {
  return entry.rollType === 'damage';
}

export function isCheckEntry(entry: RollHistoryEntry): entry is CheckHistoryEntry {
  return entry.rollType !== 'damage';
}

/** Adds a roll to the front of the history and trims to the retention limit. */
export function addRoll(
  history: readonly RollHistoryEntry[],
  entry: RollHistoryEntry,
): RollHistoryEntry[] {
  return [entry, ...history].slice(0, MAX_ROLL_HISTORY);
}

/** Adds several rolls at once, newest last in the supplied order. */
export function addRolls(
  history: readonly RollHistoryEntry[],
  entries: readonly RollHistoryEntry[],
): RollHistoryEntry[] {
  let next = [...history];
  for (const entry of entries) next = addRoll(next, entry);
  return next;
}

/** The newest few rolls, for the compact view in the Roll Tray. */
export function compactHistory(
  history: readonly RollHistoryEntry[],
  count: number = COMPACT_ROLL_HISTORY,
): RollHistoryEntry[] {
  return history.slice(0, count);
}

export function clearHistory(): RollHistoryEntry[] {
  return [];
}
